import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToolPanelFormBuilder } from '@/formbuilder/ToolPanelFormBuilder.page';
import { formulaStyles as styles } from '@/formula/formula.styles';
import { getNextFormulaIdentifier } from '@/formula/formula.utilities';
import { ML_MODEL_TYPE } from '@/main/app.constants';
import { SeeqNames } from '@seeqdev/constants';
import { Modal } from '@seeqdev/qomponents';
import type { FormElement } from '@/formbuilder/formBuilder.constants';
import type { FormulaEditorParam } from '@/formula/FormulaParametersTable.molecule';

interface AddFormulaParameterModalProps {
  onClose: () => void;
  testId: string;
  parameter?: FormulaEditorParam;
  parameters: FormulaEditorParam[];
  onSave: (item: FormulaEditorParam, original: FormulaEditorParam) => void;
}

export const AddFormulaParameterModal = ({
  testId,
  onClose,
  onSave,
  parameters,
  parameter,
}: AddFormulaParameterModalProps) => {
  const { t } = useTranslation();
  const SEARCH_TYPES = [
    SeeqNames.Types.Asset,
    SeeqNames.Types.Condition,
    SeeqNames.Types.Signal,
    SeeqNames.Types.Scalar,
    ML_MODEL_TYPE,
  ];
  const emptyItem = { name: '', id: '' };
  const [parameterIdentifier, setParameterIdentifier] = useState(parameter?.identifier ?? '');
  const [item, setItem] = useState(parameter?.item ?? emptyItem);

  useEffect(() => {
    if (!parameterIdentifier) {
      setParameterIdentifier(getNextFormulaIdentifier(_.map(parameters, 'identifier')));
    }
  }, []);

  const onSaveCallback = () => {
    if (item.id) {
      onSave(
        {
          ...parameter,
          name: parameterIdentifier,
          item,
          identifier: parameterIdentifier,
        },
        { ...(parameter as FormulaEditorParam), name: parameter?.name ?? parameter?.identifier ?? '' },
      );
      onClose();
    }
  };

  const formDataSetup: FormElement[] = [
    {
      component: 'TextFieldFormComponent',
      label: 'FORMULA.VARIABLE_IDENTIFIER',
      name: 'parameterIdentifier',
      testId: 'parameterIdentifier',
      value: parameterIdentifier,
      onChange: (value) => setParameterIdentifier(value as string),
      size: 'sm',
      xstyle: styles.lastSection,
    },
    {
      component: 'SearchWidgetFormComponent',
      name: 'parameterIdentifierItem',
      searchTypes: SEARCH_TYPES,
      allowAssetReplacement: false,
      onSearchItemClick: setItem,
      onCloseClick: () => setItem(emptyItem),
      customErrorText: 'FORMULA.NO_ITEM',
      value: item.name,
      onChange: _.noop,
      searchResultsClassNames: stylex.props(styles.parameterSearchResults).className,
    },
  ];

  return (
    <Modal
      size="sm"
      open={true}
      onClose={onClose}
      testId={testId}
      title={t('FORMULA.VARIABLE_DEFINE')}
      hideFooterButtons={true}>
      <div {...stylex.props(styles.parameterModal)}>
        <ToolPanelFormBuilder
          formDefinition={formDataSetup}
          submitFn={onSaveCallback}
          closeFn={onClose}
          toolId="add-variable"
          submitBtnId="addVariableButton"
          wrapInPanel={false}
          submitBtnLabel="ADD"
        />
      </div>
    </Modal>
  );
};
